import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Typography,
  Progress,
  Space,
  Divider,
  Alert,
  Spin,
} from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  ApiOutlined,
  BarChartOutlined,
  TeamOutlined,
  SettingOutlined,
  PlusOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import AppHeader from '../components/Header';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

const { Content } = Layout;
const { Title, Text } = Typography;

interface QuotaInfo {
  total_quota: number;
  used_quota: number;
  remaining_quota: number;
  usage_percentage: number;
}

interface DashboardData {
  company_quota?: QuotaInfo;
  applications?: {
    total: number;
    active: number;
    total_requests: number;
  };
  quick_stats?: {
    quota_usage_percentage: number;
    remaining_requests: number;
    applications_count: number;
    active_applications: number;
  };
}

const DashboardPage: React.FC = () => {
  const { user, selectedClient } = useAuthStore();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false); // Use ref to track loading state synchronously

  useEffect(() => {
    // Only load when component mounts and user is available
    if (user && user.role !== 'super_admin' && !loadingRef.current && !dashboardData) {
      loadDashboardData();
    }
  }, [user?.email, user?.role]);

  const loadDashboardData = async () => {
    // Prevent duplicate calls using ref (synchronous check)
    if (loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);
      const response = await api.get('/api/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Permission check function
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    // Admin and super_admin have access to everything
    if (user.role === 'admin' || user.role === 'super_admin') {
      return true;
    }
    
    // Check if user has the specific permission
    if (user.permissions?.includes(permission)) {
      return true;
    }
    
    // Check legacy permission format (backward compatibility)
    if (permission === 'analytics:read' && user.permissions?.includes('analytics')) {
      return true;
    }
    
    return false;
  };

  const QuickActionCard: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    buttonText: string;
    onClick: () => void;
  }> = ({ title, description, icon, buttonText, onClick }) => (
    <Card 
      style={{ 
        borderRadius: '12px',
        border: '1px solid #e6e9f4',
        height: '100%'
      }}
      bodyStyle={{ padding: '24px', textAlign: 'center' }}
    >
      <div style={{ fontSize: '32px', color: '#607afb', marginBottom: '16px' }}>
        {icon}
      </div>
      <Title level={4} style={{ color: '#0d0f1c', marginBottom: '8px', fontWeight: 600 }}>
        {title}
      </Title>
      <Text style={{ color: '#47569e', display: 'block', marginBottom: '20px' }}>
        {description}
      </Text>
      <Button type="primary" onClick={onClick} style={{ borderRadius: '6px' }}>
        {buttonText}
      </Button>
    </Card>
  );

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f8faff' }}>
      <AppHeader />
      <Content style={{ padding: '32px 48px', backgroundColor: '#f8faff' }}>
        <div style={{ marginBottom: '32px' }}>
          <Title level={2} style={{ color: '#0d0f1c', marginBottom: '8px' }}>
            Welcome back, {user?.name || user?.email}!
          </Title>
          <Text style={{ fontSize: '16px', color: '#47569e' }}>
            Here's your OCR API management overview
          </Text>
        </div>

        {/* Quota Information Section */}
        {dashboardData?.company_quota && (
          <>
            <Title level={3} style={{ color: '#0d0f1c', marginBottom: '16px' }}>
              API Quota Usage
            </Title>
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <Statistic
                    title="Total Quota"
                    value={dashboardData.company_quota.total_quota}
                    formatter={(value) => `${Number(value).toLocaleString()}`}
                    prefix={<ApiOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <Statistic
                    title="Used Quota"
                    value={dashboardData.company_quota.used_quota}
                    formatter={(value) => `${Number(value).toLocaleString()}`}
                    valueStyle={{ color: '#ff6b6b' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <Statistic
                    title="Remaining"
                    value={dashboardData.company_quota.remaining_quota}
                    formatter={(value) => `${Number(value).toLocaleString()}`}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <div>
                    <Text strong>Usage</Text>
                    <Progress
                      percent={dashboardData.company_quota.usage_percentage}
                      size="small"
                      strokeColor={
                        dashboardData.company_quota.usage_percentage > 90 
                          ? '#ff4d4f' 
                          : dashboardData.company_quota.usage_percentage > 70 
                          ? '#faad14' 
                          : '#52c41a'
                      }
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Usage Alert */}
            {dashboardData.company_quota.usage_percentage > 80 && (
              <Alert
                message={
                  dashboardData.company_quota.usage_percentage > 90
                    ? "High Usage Alert"
                    : "Usage Warning"
                }
                description={
                  dashboardData.company_quota.usage_percentage > 90
                    ? `You've used ${dashboardData.company_quota.usage_percentage.toFixed(1)}% of your quota. Consider upgrading your plan.`
                    : `You've used ${dashboardData.company_quota.usage_percentage.toFixed(1)}% of your quota. Monitor your usage carefully.`
                }
                type={dashboardData.company_quota.usage_percentage > 90 ? "error" : "warning"}
                showIcon
                style={{ marginBottom: '32px', borderRadius: '8px' }}
              />
            )}
          </>
        )}

        {/* Loading State */}
        {loading && !dashboardData && (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spin size="large" />
          </div>
        )}

        {/* Quick Actions Section */}
        {(hasPermission('api_management:read') || 
          hasPermission('document_types:read') || 
          hasPermission('user_management:read') || 
          hasPermission('user_management:write') || 
          hasPermission('analytics:read') || 
          hasPermission('request_logs:read')) && (
          <>
            <Title level={3} style={{ color: '#0d0f1c', marginBottom: '24px' }}>
              Quick Actions
            </Title>
            
            <Row gutter={[24, 24]} style={{ marginBottom: '48px' }}>
              {hasPermission('api_management:read') && (
                <Col xs={24} sm={12} lg={6}>
                  <QuickActionCard
                    title="API Management"
                    description="Manage your applications, API keys, and quotas"
                    icon={<AppstoreOutlined />}
                    buttonText="Manage APIs"
                    onClick={() => navigate('/api-management')}
                  />
                </Col>
              )}
              {hasPermission('document_types:read') && (
                <Col xs={24} sm={12} lg={6}>
                  <QuickActionCard
                    title="Document Types"
                    description="Configure and manage OCR document types"
                    icon={<FileTextOutlined />}
                    buttonText="Manage Types"
                    onClick={() => navigate('/document-types')}
                  />
                </Col>
              )}
              {(hasPermission('user_management:read') || hasPermission('user_management:write')) && (
                <Col xs={24} sm={12} lg={6}>
                  <QuickActionCard
                    title="User Management"
                    description="Add and manage team members"
                    icon={<TeamOutlined />}
                    buttonText="Manage Users"
                    onClick={() => navigate('/users')}
                  />
                </Col>
              )}
              {hasPermission('user_management:write') && (
                <Col xs={24} sm={12} lg={6}>
                  <QuickActionCard
                    title="Role Management"
                    description="Create and manage user roles"
                    icon={<SettingOutlined />}
                    buttonText="Manage Roles"
                    onClick={() => navigate('/roles')}
                  />
                </Col>
              )}
              {hasPermission('analytics:read') && (
                <Col xs={24} sm={12} lg={6}>
                  <QuickActionCard
                    title="Analytics"
                    description="View usage analytics and reports"
                    icon={<BarChartOutlined />}
                    buttonText="View Analytics"
                    onClick={() => navigate('/analytics')}
                  />
                </Col>
              )}
              {hasPermission('request_logs:read') && (
                <Col xs={24} sm={12} lg={6}>
                  <QuickActionCard
                    title="Request Logs"
                    description="Monitor and analyze OCR request logs"
                    icon={<HistoryOutlined />}
                    buttonText="View Logs"
                    onClick={() => navigate('/logs')}
                  />
                </Col>
              )}
              <Col xs={24} sm={12} lg={6}>
                <QuickActionCard
                  title="Billing & Credits"
                  description="Manage credits and view billing information"
                  icon={<DollarOutlined />}
                  buttonText="View Billing"
                  onClick={() => navigate('/billing')}
                />
              </Col>
            </Row>
          </>
        )}

        {/* Application Stats */}
        {dashboardData?.applications && (
          <>
            <Title level={3} style={{ color: '#0d0f1c', marginBottom: '16px' }}>
              Application Overview
            </Title>
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={8}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <Statistic
                    title="Total Applications"
                    value={dashboardData.applications.total}
                    prefix={<AppstoreOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <Statistic
                    title="Active Applications"
                    value={dashboardData.applications.active}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<ApiOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card style={{ borderRadius: '12px', border: '1px solid #e6e9f4' }}>
                  <Statistic
                    title="Total Requests"
                    value={dashboardData.applications.total_requests}
                    formatter={(value) => `${Number(value).toLocaleString()}`}
                    prefix={<BarChartOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default DashboardPage;
